import { useState } from 'react';

interface LimitExceededState {
  isOpen: boolean;
  limitType: 'clients' | 'processes' | 'users' | 'terms' | null;
  limit: number;
  open: (limitType: 'clients' | 'processes' | 'users' | 'terms', limit: number) => void;
  close: () => void;
}

export function useLimitExceeded(): LimitExceededState {
  const [isOpen, setIsOpen] = useState(false);
  const [limitType, setLimitType] = useState<'clients' | 'processes' | 'users' | 'terms' | null>(null);
  const [limit, setLimit] = useState(0);

  const open = (type: 'clients' | 'processes' | 'users' | 'terms', limitValue: number) => {
    setLimitType(type);
    setLimit(limitValue);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setLimitType(null);
    setLimit(0);
  };

  return {
    isOpen,
    limitType,
    limit,
    open,
    close,
  };
}

// Global store for limit exceeded modal (used in api interceptor)
let globalLimitExceededHandler: ((limitType: 'clients' | 'processes' | 'users' | 'terms', limit: number) => void) | null = null;

export function setGlobalLimitExceededHandler(
  handler: (limitType: 'clients' | 'processes' | 'users' | 'terms', limit: number) => void
) {
  globalLimitExceededHandler = handler;
}

export function getGlobalLimitExceededHandler() {
  return globalLimitExceededHandler;
}

const LIMIT_TYPES = ['clients', 'processes', 'users', 'terms'] as const;
type LimitKind = (typeof LIMIT_TYPES)[number];

function parseLimitTypeFromMessage(message: string): LimitKind | null {
  if (message.includes('limite de clientes')) return 'clients';
  if (message.includes('limite de processos')) return 'processes';
  if (message.includes('limite de usuários')) return 'users';
  if (message.includes('limite de termos')) return 'terms';
  return null;
}

function getAxiosErrorData(error: unknown): {
  status?: number;
  data?: Record<string, unknown>;
} | null {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const r = (error as { response?: { status?: number; data?: unknown } }).response;
  if (!r || typeof r.data !== 'object' || r.data === null) {
    return { status: r?.status, data: undefined };
  }
  return { status: r.status, data: r.data as Record<string, unknown> };
}

/** Resposta de erro da API ou corpo reduzido só com `message` (ex.: filtro legado). */
export function isLimitExceededApiError(error: unknown): boolean {
  const { status, data } = getAxiosErrorData(error) ?? {};
  if (!data) return false;

  if (data.error === 'LIMIT_EXCEEDED' || data.code === 'BILLING_LIMIT_EXCEEDED') {
    return true;
  }

  if (status === 403 && typeof data.message === 'string') {
    return parseLimitTypeFromMessage(data.message) != null;
  }

  return false;
}

/** Abre o LimitExceededModal quando a API sinaliza limite do plano. Retorna true se tratou o erro. */
export function tryHandleLimitExceededFromApiError(error: unknown): boolean {
  if (!isLimitExceededApiError(error)) {
    return false;
  }

  const { data } = getAxiosErrorData(error) ?? {};
  if (!data) return false;

  const handler = getGlobalLimitExceededHandler();
  if (!handler) {
    return false;
  }

  let limitTypeRaw: unknown = data.limitType;
  if (typeof limitTypeRaw !== 'string' || !LIMIT_TYPES.includes(limitTypeRaw as LimitKind)) {
    if (typeof data.message === 'string') {
      limitTypeRaw = parseLimitTypeFromMessage(data.message);
    }
  }

  if (typeof limitTypeRaw !== 'string' || !LIMIT_TYPES.includes(limitTypeRaw as LimitKind)) {
    return false;
  }

  let limitNum = 0;
  if (typeof data.limit === 'number' && Number.isFinite(data.limit)) {
    limitNum = data.limit;
  }

  handler(limitTypeRaw as LimitKind, limitNum);
  return true;
}

